import { useTeamTree, type TeamNode } from '@internal/backstage-plugin-common-react';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

import { LinkButton } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';

import Box from '@mui/material/Box';

import { useGetCategoriesData } from '../../hooks/useGetCategoriesData';
import { getKPIItems, getScorecardItems, toStandardISO } from '../../utils';
import { OperationalExcellenceTable } from '../../widgets/OperationalExcellenceTable';

const excludeList = new Set(['Velocity', 'Repository Ownership']);

const flattenTeamIds = (node?: TeamNode): string[] => {
  if (!node || !node.team_id) return [];
  return [node.team_id, ...(node.children ?? []).flatMap(flattenTeamIds)];
};

export const TeamEngineeringInsightsSummary = () => {
  const { entity } = useEntity();
  const orgId = entity.metadata.annotations?.['tomtom.com/org-id'];

  const { tree } = useTeamTree(orgId);

  const endDate = useMemo(() => toStandardISO(DateTime.now()), []);
  const startDate = useMemo(
    () => toStandardISO(DateTime.now().minus({ months: 5 }).startOf('month')),
    [],
  );

  const teamIds = useMemo(() => flattenTeamIds(tree), [tree]);

  const { value: categoriesData } = useGetCategoriesData(
    tree,
    startDate,
    endDate,
    'month',
  );

  const scorecardItems = useMemo(
    () => (categoriesData ? getScorecardItems(categoriesData) : null),
    [categoriesData],
  );

  const kpiItems = useMemo(
    () => (categoriesData ? getKPIItems(categoriesData) : null),
    [categoriesData],
  );

  const filteredKpiItems = useMemo(
    () => (kpiItems ?? []).filter(sItem => !excludeList.has(sItem.label)),
    [kpiItems],
  );

  const namespace = entity.metadata.namespace ?? 'default';
  const engineeringInsightsHref = `/catalog/${namespace}/group/${entity.metadata.name}/engineering-insights`;

  const actions = (
    <Box display="flex">
      <LinkButton
        variant="outlined"
        color="primary"
        to={engineeringInsightsHref}
      >
        View Engineering Insights
      </LinkButton>
    </Box>
  );

  if (!scorecardItems?.length) {
    return null;
  }

  return (
    <OperationalExcellenceTable
      scorecardItems={filteredKpiItems}
      teamIds={teamIds}
      startDate={startDate}
      endDate={endDate}
      monthsToShow={6}
      actions={actions}
      enableLinks
    />
  );
};
